import React, { useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useGetAdminFeatureUseSummaryQuery } from '@store/api/adminApi'
import type { FeatureUseSummaryRange } from '@store/api/adminApi'

export const FeatureUseSummaryPanel: React.FC = () => {
  const [range, setRange] = useState<FeatureUseSummaryRange>('30d')
  const { data, isLoading, isFetching, isError } = useGetAdminFeatureUseSummaryQuery({ range })

  const summary = data
  const rangeDisabled = isLoading || isFetching

  return (
    <Card sx={{ mb: 4 }} data-testid="feature-use-summary">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Feature use (first-party)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Signed-in feature_use events from CigarTrack analytics — not Google Analytics.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 120 }} disabled={rangeDisabled}>
            <InputLabel id="feature-use-range-label">Range</InputLabel>
            <Select
              labelId="feature-use-range-label"
              label="Range"
              value={range}
              disabled={rangeDisabled}
              onChange={(e) => setRange(e.target.value as FeatureUseSummaryRange)}
              data-testid="feature-use-range-select"
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && (
          <Alert severity="error">Could not load feature-use summary. Please try again.</Alert>
        )}

        {!isLoading && !isError && summary && !summary.enabled && (
          <Alert severity="info" data-testid="feature-use-summary-disabled">
            {summary.disabledReason ??
              'Analytics is disabled. Feature-use summary is unavailable until analytics collection is enabled.'}
          </Alert>
        )}

        {!isLoading && !isError && summary?.enabled && summary.total === 0 && (
          <Alert severity="info" data-testid="feature-use-summary-empty">
            No feature_use events in the last {summary.rangeDays} days yet. Usage will appear here once members
            interact with tracked features (for example nav tabs or smoke-session CTAs).
          </Alert>
        )}

        {!isLoading && !isError && summary?.enabled && summary.total > 0 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {summary.total.toLocaleString()} events in the last {summary.rangeDays} days
            </Typography>
            <Stack spacing={1}>
              {summary.byEventName.map((row) => (
                <Box
                  key={row.bucketId || row.eventName}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" fontFamily="monospace">{row.eventName}</Typography>
                  <Typography variant="body2" fontWeight={600}>{row.count.toLocaleString()}</Typography>
                </Box>
              ))}
            </Stack>
            {summary.byViewport && (
              <Box data-testid="feature-use-viewport-split">
                <Typography variant="subtitle2" gutterBottom>By viewport</Typography>
                <Stack direction="row" spacing={3}>
                  <Typography variant="body2">xs: {summary.byViewport.xs.toLocaleString()}</Typography>
                  <Typography variant="body2">md+: {summary.byViewport.mdPlus.toLocaleString()}</Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default FeatureUseSummaryPanel
